import React, { useState } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

const DependencyLines = ({ 
  swimlaneData, 
  cardPositions,
  getCardInfoByTitle,
  highlightedDependency
}) => {
  const theme = useTheme();
  const [hoveredLine, setHoveredLine] = useState(null);
  
  const getPathD = (startCardPos, endCardPos, isInterSwimlane = false) => {
    if (!startCardPos || !endCardPos) return '';

    // Calculate the connection points on the sides of the cards
    // Right-middle of source card
    const startX = startCardPos.x + (startCardPos.width / 2);
    const startY = startCardPos.y;
    
    // Left-middle of target card
    const endX = endCardPos.x - (endCardPos.width / 2);
    const endY = endCardPos.y;

    if (isInterSwimlane) {
      // For cards in different swimlanes, use a curved path with control points
      const controlX1 = startX + 50;
      const controlY1 = startY;
      const controlX2 = endX - 50;
      const controlY2 = endY;
      return `M ${startX} ${startY} C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${endX} ${endY}`;
    }
    
    // For cards in the same swimlane, use a simple quadratic curve
    const midX = (startX + endX) / 2;
    return `M ${startX} ${startY} Q ${midX} ${startY + (endY > startY ? 10 : -10)}, ${endX} ${endY}`;
  };
  
  return (
    <Box 
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'auto', // Changed to allow interaction
        zIndex: 0
      }}
    >
      <svg
        width="100%"
        height="100%"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          pointerEvents: 'auto', // Changed to allow interaction
          zIndex: 0
        }}
      >
        {swimlaneData.flatMap((lane) =>
          lane.cards.flatMap(card =>
            (Array.isArray(card.dependencies) ? card.dependencies : [])
            .map(depTitle => {
              const fromCardInfo = getCardInfoByTitle(depTitle);
              if (!fromCardInfo || !fromCardInfo.cardId) {
                return null; 
              }
              const fromCardId = fromCardInfo.cardId;
              const toCardId = card.id;

              const fromPos = cardPositions[fromCardId];
              const toPos = cardPositions[toCardId];

              if (!fromPos || !toPos) {
                return null;
              }
              
              // Determine if the line crosses between swimlanes
              const fromLane = swimlaneData.find(l => l.cards.some(c => c.id === fromCardId));
              const toLane = swimlaneData.find(l => l.cards.some(c => c.id === toCardId));
              const isInterSwimlane = fromLane && toLane && fromLane.title !== toLane.title;
              
              const pathD = getPathD(fromPos, toPos, isInterSwimlane);

              if (!pathD) {
                return null;
              }

              // Use the destination card's border color for the lines
              const lineColor = card.borderColor || theme.palette.primary.main;
              const lineId = `${fromCardId}-${toCardId}`;
              
              // Check if this line should be highlighted based on chip hover
              const isHighlightedByChip = highlightedDependency && 
                ((highlightedDependency.from === fromCardId && highlightedDependency.to === toCardId) || 
                 (highlightedDependency.to === fromCardId && highlightedDependency.from === toCardId));
              
              // Determine if this line should be highlighted (either by chip hover or direct line hover)
              const isDirectlyHovered = hoveredLine === lineId;
              const shouldHighlight = isHighlightedByChip || isDirectlyHovered;
              
              // Keep the original line color even when highlighted
              return (
                <g key={lineId}>
                  {/* Invisible wider path to make hovering easier */}
                  <path
                    d={pathD}
                    stroke="transparent"
                    strokeWidth="15"
                    fill="none"
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={() => setHoveredLine(lineId)}
                    onMouseLeave={() => setHoveredLine(null)}
                  />
                  {/* Visible path with styling */}
                  <path
                    d={pathD}
                    stroke={lineColor} // Always use the original line color
                    strokeWidth={shouldHighlight ? "6" : "3"} // Thicker on hover
                    fill="none"
                    strokeDasharray={shouldHighlight ? "5,3" : "5,5"} // Different dash pattern on hover
                    strokeOpacity={shouldHighlight ? "1" : "0.7"} // Full opacity on hover
                    style={{
                      pointerEvents: 'none', // The invisible path handles events
                      transition: 'all 0.2s ease'
                    }}
                  />
                </g>
              );
            })
          )
        )}
      </svg>
    </Box>
  );
};

export default DependencyLines;
